import Vue from 'vue';
import { DeviceEdit, Device } from '@/interfaces/locations';
import { Commit } from 'vuex';

interface LuminaireState {
	luminaires: {
		[id: number]: Device;
	};
	selectedLuminaire: Device | null;
}

export default {
	namespaced: true,
	state: {
		luminaires: {},
		selectedLuminaire: null,
	} as LuminaireState,
	getters: {
		getLuminaires: (state: LuminaireState): Device[] => {
			return Object.values(state.luminaires);
		},

		getFloorLuminaires:
			(state: LuminaireState) =>
			(floorId: number): Device[] => {
				return Object.values(state.luminaires).filter(
					(luminaire) => luminaire.fk_area === floorId || luminaire.fk_area === null
				);
			},

		getLuminaire:
			(state: LuminaireState) =>
			(id: number): Device | undefined => {
				return state.luminaires[id];
			},

		getSelectedLuminaires: (state: LuminaireState): Device | null => {
			return state.selectedLuminaire;
		},
	},
	mutations: {
		SET_LUMINAIRES(state: LuminaireState, luminaires: Device[]): void {
			const ret: { [id: number]: Device } = {};
			state.luminaires = luminaires.reduce((obj, luminaire) => {
				obj[luminaire.id] = luminaire;
				return obj;
			}, ret);
		},

		ADD_LUMINAIRE(state: LuminaireState, luminaire: Device): void {
			Vue.set(state.luminaires, luminaire.id, luminaire);
		},

		CHANGE_LUMINAIRE(state: LuminaireState, luminaire: Device & { id: number }): void {
			const oldLuminaire = state.luminaires[luminaire.id] || {};
			Vue.set(state.luminaires, luminaire.id, { ...oldLuminaire, ...luminaire });
		},

		CHANGE_SELECTED_LUMINAIRE(state: LuminaireState, luminaire: Device): void {
			state.selectedLuminaire = luminaire;
		},

		REMOVE_LUMINAIRE(state: LuminaireState, luminaireId: number): void {
			Vue.delete(state.luminaires, luminaireId);
		},

		CLEAN_LUMINAIRES(state: LuminaireState): void {
			state.luminaires = {};
		},

		CLEAN_SELECTED_LUMINAIRES(state: LuminaireState): void {
			state.selectedLuminaire = null;
		},
	},
	actions: {
		async fetchLuminaires({ commit }: { commit: Commit }): Promise<void> {
			const response = await Vue.axios.get(
				`${process.env.VUE_APP_API_URL}/devices/luminaires/show_type`
			);

			if (response && response.status === 200) {
				commit('SET_LUMINAIRES', response.data.data);
			}
		},

		async fetchLuminaire({ commit }: { commit: Commit }, { id }: { id: number }): Promise<void> {
			const response = await Vue.axios.get(
				`${process.env.VUE_APP_API_URL}/devices/luminaires/${id}`
			);
			if (response && response.status === 200) {
				commit('CHANGE_LUMINAIRE', response.data.data);
			}
		},

		async addLuminaire(
			{ commit }: { commit: Commit },
			{ payload, returnData = false }: { payload: DeviceEdit; returnData: boolean }
		): Promise<void | Device> {
			const response = await Vue.axios.post(
				`${process.env.VUE_APP_API_URL}/devices/luminaires`,
				payload
			);
			if (response && response.status === 201) {
				commit('ADD_LUMINAIRE', response.data.data);
				if (returnData) {
					return response.data.data;
				}
			}
		},

		async modifyLuminaire(
			{ commit }: { commit: Commit },
			{ id, payload }: { id: number; payload: Device }
		): Promise<void> {
			const response = await Vue.axios.put(
				`${process.env.VUE_APP_API_URL}/devices/luminaires/${id}`,
				payload
			);
			if (response && response.status === 200) {
				commit('CHANGE_LUMINAIRE', response.data.data);
			}

			const offset = { light_level_offset: payload.light_level_offset };

			if (offset && offset.light_level_offset !== response.data.data.light_level_offset) {
				const offsetResponse = await Vue.axios.put(
					`${process.env.VUE_APP_API_URL}/rules/offset/luminaires/${id}`,
					offset
				);
				if (offsetResponse && offsetResponse.status === 200) {
					commit('CHANGE_LUMINAIRE', offsetResponse.data.data);
				}
			}
		},

		async deleteLuminaire({ commit }: { commit: Commit }, { id }: { id: number }): Promise<void> {
			const response = await Vue.axios.delete(
				`${process.env.VUE_APP_API_URL}/devices/luminaires/${id}`
			);
			if (response && response.status === 200) {
				commit('REMOVE_LUMINAIRE', id);
			}
		},

		selectLuminaire({ commit }: { commit: Commit }, { luminaire }: { luminaire: Device }): void {
			if (luminaire) {
				commit('CHANGE_SELECTED_LUMINAIRE', luminaire);
			}
		},

		cleanLuminaire({ commit }: { commit: Commit }): void {
			commit('CLEAN_LUMINAIRES');
		},

		cleanSelectedLuminaire({ commit }: { commit: Commit }): void {
			commit('CLEAN_SELECTED_LUMINAIRE');
		},

		cleanAllLuminaire({ commit }: { commit: Commit }): void {
			commit('CLEAN_LUMINAIRES');
			commit('CLEAN_SELECTED_LUMINAIRES');
		},
	},
};
